// src/components/audio/MiniPlayerBar.tsx
import React from 'react'
import { useAudio } from '@/components/providers/AudioProvider'
import { ECHO_COLORS } from '@/lib/intelligence'

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]

function formatTime(s: number) {
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`
}

export function MiniPlayerBar() {
  const {
    activeBriefing, isPlaying, currentTime, duration,
    playbackSpeed, isMuted, isLoading,
    togglePlay, seek, toggleMute, setSpeed, stop,
  } = useAudio()

  const [showSpeed, setShowSpeed] = React.useState(false)

  if (!activeBriefing) return null

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div style={{
      position: 'fixed',
      bottom: 0, left: 0, right: 0,
      zIndex: 50,
      // Echo card treatment on the bar itself
      background: 'var(--color-background-primary)',
      borderTop: ECHO_COLORS.cardBorderTop,
      backdropFilter: 'blur(12px)',
    }}>
      {/* Top-edge glow line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: ECHO_COLORS.cardTopLine,
        pointerEvents: 'none',
      }} />

      {/* Progress bar — full width, sits at very top */}
      <div style={{ height: 2, background: 'rgba(120,180,255,0.1)', position: 'relative' }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #6600FF, rgba(0,200,160,0.8))',
          transition: 'width 0.5s linear',
        }} />
        <input
          type="range" min="0" max={duration || 100} value={currentTime} step="0.5"
          onChange={e => seek(parseFloat(e.target.value))}
          style={{ position: 'absolute', inset: 0, width: '100%', opacity: 0, cursor: 'pointer', height: '100%', margin: 0 }}
        />
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 20px', maxWidth: 1280, margin: '0 auto',
      }}>
        {/* Play/pause */}
        <button
          onClick={togglePlay}
          style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#6600FF', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, opacity: isLoading ? 0.6 : 1,
          }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          ) : isPlaying ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white" style={{ marginLeft: 1 }} aria-hidden="true">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
        </button>

        {/* Title + time */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)',
            margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {activeBriefing.title}
          </p>
          <p style={{ fontSize: 10, color: 'var(--color-text-secondary)', margin: '1px 0 0' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </p>
        </div>

        {/* Controls right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {/* Mute */}
          <button onClick={toggleMute} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', padding: 4 }} aria-label={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>

          {/* Speed */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSpeed(s => !s)}
              style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500,
                background: 'none', border: '0.5px solid var(--color-border-tertiary)',
                color: 'var(--color-text-secondary)', cursor: 'pointer',
              }}
            >
              {playbackSpeed}×
            </button>
            {showSpeed && (
              <div style={{
                position: 'absolute', bottom: '100%', right: 0, marginBottom: 4,
                background: 'var(--color-background-primary)',
                border: '0.5px solid var(--color-border-secondary)',
                borderRadius: 8, overflow: 'hidden',
              }}>
                {SPEED_OPTIONS.map(s => (
                  <button key={s}
                    onClick={() => { setSpeed(s); setShowSpeed(false) }}
                    style={{
                      display: 'block', width: '100%', padding: '5px 14px',
                      textAlign: 'left', fontSize: 11,
                      background: playbackSpeed === s ? 'var(--color-background-secondary)' : 'none',
                      border: 'none', cursor: 'pointer',
                      color: 'var(--color-text-primary)',
                      fontWeight: playbackSpeed === s ? 500 : 400,
                    }}
                  >{s}×</button>
                ))}
              </div>
            )}
          </div>

          {/* Stop */}
          <button onClick={stop} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', padding: 4 }} aria-label="Stop">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}